import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  useLazyDownloadTeamHierarchyQuery,
  useUploadTeamHierarchyMutation,
  useGetUserAgentsMutation,
} from "./teamsMutation";

const parseCSV = (csvText) => {
  if (!csvText || typeof csvText !== "string") return { headers: [], data: [] };
  const lines = csvText.trim().split("\n");
  if (lines.length === 0) return { headers: [], data: [] };

  const headers = lines[0].split(",").map((h) => h.trim());
  const data = lines
    .slice(1)
    .map((line) => {
      const values = line.split(",").map((v) => v.trim());
      if (values.length !== headers.length || line.trim() === "") {
        return null;
      }
      const entry = {};
      headers.forEach((header, index) => {
        entry[header] = values[index];
      });
      return entry;
    })
    .filter((entry) => entry !== null);

  return { headers, data };
};

const useTeamHierarchyQueries = (initialPage = 1, initialLimit = 10) => {
  const [
    triggerDownload,
    {
      data: rawCsvData,
      isLoading: isDownloadLoading,
      error: downloadError,
      isFetching: isDownloadFetching,
    },
  ] = useLazyDownloadTeamHierarchyQuery();
  const [
    uploadTeamHierarchy,
    { isLoading: isUploadLoading, error: uploadError },
  ] = useUploadTeamHierarchyMutation();

  const [parsedData, setParsedData] = useState({ headers: [], data: [] });
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (rawCsvData) {
      const result = parseCSV(rawCsvData);
      setParsedData(result);
      setSearchTerm("");
      setCurrentPage(1);
    }
  }, [rawCsvData]);

  const filteredData = useMemo(() => {
    if (!searchTerm) {
      return parsedData.data;
    }
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    return parsedData.data.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(lowerCaseSearchTerm)
      )
    );
  }, [parsedData.data, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  const totalCount = filteredData.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePageChange = useCallback(
    (newPage) => {
      setCurrentPage(Math.max(1, Math.min(newPage, totalPages || 1)));
    },
    [totalPages]
  );

  const handleLimitChange = useCallback((newLimit) => {
    const validatedLimit = Math.max(1, newLimit);
    setItemsPerPage(validatedLimit);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((query) => {
    setSearchTerm(query || "");
    setCurrentPage(1);
  }, []);

  const handleDownload = useCallback(() => {
    const formData = new FormData();
    formData.append("file_name", "team_hierarchy");
    triggerDownload(formData);
  }, [triggerDownload]);

  const handleUpload = useCallback(
    async (formData) => {
      try {
        formData.append("file_name", "team_hierarchy");
        const result = await uploadTeamHierarchy(formData);

        if (result.data?.__isApiValidationError) {
          throw {
            name: "ApiValidationError",
            data: {
              skippedRecords: result.data.skippedRecords,
              validationErrors: result.data.validationErrors,
            },
          };
        }

        return result.data;
      } catch (error) {
        if (error?.name === "ApiValidationError") {
          throw error;
        }
        throw error;
      }
    },
    [uploadTeamHierarchy]
  );

  return {
    // Download related
    hierarchyHeaders: parsedData.headers,
    hierarchyData: paginatedData,
    totalHierarchyCount: totalCount,
    isDownloadLoading: isDownloadLoading || isDownloadFetching,
    downloadError,
    handleDownload,

    // Upload related
    handleUpload,
    isUploadLoading,
    uploadError,

    // Pagination related
    currentPage,
    itemsPerPage,
    totalPages,
    handlePageChange,
    handleLimitChange,

    // Search related
    searchTerm,
    handleSearchChange,

    // Raw data for export
    rawCsvData,
  };
};

export default useTeamHierarchyQueries;

export const useTeamUsersQueries = (initialPage = 1, initialLimit = 10) => {
  const location = useLocation();
  const isActive = location.pathname === "/teams";
  const hasInitializedRef = useRef(false);
  const lastParamsRef = useRef({ page: null, limit: null, searchQuery: null });

  // Mark as initialized immediately when page becomes active for the first time
  if (isActive && !hasInitializedRef.current) {
    hasInitializedRef.current = true;
  }

  const [getUserAgents, { data: userAgentsData, isLoading, error }] =
    useGetUserAgentsMutation();

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchUserAgents = useCallback(
    (page, limit, search_string) => {
      getUserAgents({ page, limit, search_string });
    },
    [getUserAgents]
  );

  useEffect(() => {
    // Only fetch if:
    // 1. Page is active
    // 2. Has been initialized
    // 3. Either this is the first fetch (lastParamsRef.page is null) or parameters changed
    if (isActive && hasInitializedRef.current) {
      const isFirstFetch = lastParamsRef.current.page === null;
      const paramsChanged =
        lastParamsRef.current.page !== currentPage ||
        lastParamsRef.current.limit !== itemsPerPage ||
        lastParamsRef.current.searchQuery !== searchQuery;

      if (isFirstFetch || paramsChanged) {
        lastParamsRef.current = {
          page: currentPage,
          limit: itemsPerPage,
          searchQuery,
        };
        fetchUserAgents(currentPage, itemsPerPage, searchQuery);
      }
    }
  }, [currentPage, itemsPerPage, searchQuery, fetchUserAgents, isActive]);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(Math.max(1, newPage));
  }, []);

  const handleLimitChange = useCallback((newLimit) => {
    const validatedLimit = Math.max(1, newLimit);
    setItemsPerPage(validatedLimit);
    setCurrentPage(1);
  }, []);

  const handleSearchQueryChange = useCallback((query) => {
    setSearchQuery(query || "");
    setCurrentPage(1);
  }, []);

  const result = useMemo(
    () => ({
      agents: userAgentsData?.agents || [],
      totalCount: userAgentsData?.totalCount || 0,
      totalPages: userAgentsData?.totalPages || 0,
      currentPage: userAgentsData?.currentPage || currentPage,
      itemsPerPage,
      isLoading,
      error,
      handlePageChange,
      handleLimitChange,
      handleSearchQueryChange,
    }),
    [
      userAgentsData,
      currentPage,
      itemsPerPage,
      isLoading,
      error,
      handlePageChange,
      handleLimitChange,
      handleSearchQueryChange,
    ]
  );

  return result;
};
